import React, { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ElementUpdateTypes, MultiPageVideoModel } from '@bynder-studio/render-core';

import { useToggleExpand } from 'packages/common/editor/helpers/groups';
import { ElementRenderer } from './element-renderer';
import { createTrackRepresentations, prepareElements } from './track-renderer';
import { Track } from 'packages/common/editor/timeline/tracks/track/Track';
import { Element } from 'packages/common/editor/timeline/tracks/element/Element';
import { computeResizeSnapResults, getDropCommand, leftToStartFrame, TRACK_HEIGHT } from './helper';

import { useResizeLine, useResizeStats } from 'packages/common/editor/timeline/tracks/dragging/useResizing';
import { computeDropResults } from 'packages/common/editor/timeline/tracks/dragging/dragging';
import { useSnap } from 'packages/common/editor/timeline/tracks/dragging/useSnap';

import { isCtrlKey, isShiftKey } from 'packages/helpers/hotKeys';
import { Timestamps } from 'packages/common/editor/timeline/tracks/timestamps/Timestamps';
import { ResizeStats } from 'packages/common/editor/timeline/tracks/resize-stats/ResizeStats';
import Playhead from 'packages/common/editor/timeline/tracks/playhead/Playhead';
import useEditor from 'packages/hooks/useEditor';
import useForceUpdate from 'packages/hooks/useForceUpdate';
import useEditorTimeframe from 'packages/hooks/useEditorTimeframe';
import { getDesUpdateForChildRemoval } from 'packages/common/editor/timeline/timeline-actions/components/desUtils';
import {
    TimelineElements,
    TimelineElementsContainer,
    TimelineGroupBackground,
    TimelineOverflowOverlay,
    TimelineResizingLine,
    TimelineSnapLine,
    TimelineTimestampsContainer,
    TimelineTracksContainer,
    TimelineTracksList,
    TimelineTracksWrapper,
} from './Tracks.styled';
import { DraggingProvider } from 'packages/common/editor/timeline/tracks/dragging/useDragging';
import useAccessRights from 'packages/hooks/useAccessRights';
import features from 'packages/configs/features';
import { useShiftSelection, flattenTrackItemIds } from 'packages/common/editor/helpers/useShiftSelection';

const elementRenderer = new ElementRenderer(TRACK_HEIGHT);

type Props = {
    elementContainerRef: RefObject<HTMLDivElement | null>;
    timestampsContainerRef: RefObject<HTMLDivElement | null>;
    elementWrapperRef: RefObject<HTMLDivElement | null>;
};

const Tracks = ({ elementContainerRef, timestampsContainerRef, elementWrapperRef }: Props) => {
    const { isPlatformAllowed } = useAccessRights();
    const { creativeModel, manipulationRenderer } = useEditor();
    const { duration, timelineDuration } = useEditorTimeframe(creativeModel as MultiPageVideoModel);
    const forceUpdate = useForceUpdate();
    const data = useRef({ elements: [] });
    const isDragging = useRef(false);
    const isResizing = useRef(false);
    const { elements } = data.current;
    const [currentSize, setCurrentSize] = useState(0);

    // POC - move multiple selected elements
    const isMultipleSelectionMovePocEnabled = isPlatformAllowed([features.MULTIPLE_SELECTION_MOVE_POC]);

    const elementsUpdateListener = useCallback(() => {
        data.current.elements = creativeModel.getElements().slice();
        forceUpdate();
    }, [creativeModel, forceUpdate]);

    useEffect(() => {
        if (creativeModel) {
            data.current.elements = creativeModel.getElements();
            forceUpdate();
        }
    }, [creativeModel, forceUpdate, currentSize]);

    useEffect(() => {
        if (!creativeModel) {
            return;
        }

        setCurrentSize(creativeModel.getCurrentPageIndex());

        const setCurrentPage = (index: number) => {
            setCurrentSize(index);
            data.current.elements = creativeModel.getElements();
            forceUpdate();
        };

        creativeModel.on('currentPageChange', setCurrentPage);

        return () => creativeModel.off('currentPageChange', setCurrentPage);
    }, [creativeModel, forceUpdate]);

    useEffect(() => {
        if (!creativeModel) {
            return;
        }

        const unsubscribeAnimation = creativeModel.onElementsPartialUpdate(
            ElementUpdateTypes.ANIMATIONS,
            elementsUpdateListener,
        );
        const unsubscribeAnimationIn = creativeModel.onElementsPartialUpdate(
            ElementUpdateTypes.ANIMATION_IN,
            elementsUpdateListener,
        );
        const unsubscribeAnimationOut = creativeModel.onElementsPartialUpdate(
            ElementUpdateTypes.ANIMATION_OUT,
            elementsUpdateListener,
        );
        creativeModel.on('elementsTreeUpdated', elementsUpdateListener);

        return () => {
            unsubscribeAnimation();
            unsubscribeAnimationIn();
            unsubscribeAnimationOut();
            creativeModel.off('elementsTreeUpdated', elementsUpdateListener);
        };
    }, [creativeModel, elementsUpdateListener]);

    const [expandedGroupIds, toggleGroupExpand] = useToggleExpand([]);
    const [expandedAnimationElementsIds, toggleAnimation] = useToggleExpand([]);

    const handleAnimationExpand = useCallback(
        (item, e) => {
            e.stopPropagation();
            toggleAnimation(item.element.id);
        },
        [toggleAnimation],
    );
    const handleGroupExpand = useCallback((item) => toggleGroupExpand(item.element.id), [toggleGroupExpand]);

    const tracksRef = useRef(null);
    const timelineTracksContainerRef = useRef(null);

    const [resizeLineRef, onLineResize, onLineResizeEnd] = useResizeLine();
    const [resizeStatsRef, onStatsResize, onStatsResizeEnd] = useResizeStats(duration);
    const [snapLineRef, snapOnDrag, snapOnDragEnd, getSnapPoint] = useSnap(elementContainerRef);

    const { visualItems, audioItems, items } = useMemo(
        () => prepareElements(elements, expandedGroupIds, expandedAnimationElementsIds),
        [elements, expandedGroupIds, expandedAnimationElementsIds],
    );
    const tracks = useMemo(() => createTrackRepresentations(visualItems), [visualItems]);
    const audioTracks = useMemo(() => createTrackRepresentations(audioItems), [audioItems]);
    const renderAbleItems = useMemo(() => elementRenderer.createRenderAbleItems(items, duration), [items, duration]);

    const { handleShiftClick, setAnchor } = useShiftSelection(manipulationRenderer);

    const handleSelection = useCallback(
        (item, evt) => {
            evt.preventDefault();
            evt.stopPropagation();

            if (isShiftKey(evt)) {
                const visibleIds = flattenTrackItemIds(items);
                handleShiftClick(item.element.id, visibleIds);
            } else {
                const isMultiSelectMode = isCtrlKey(evt);
                manipulationRenderer.selectElement(item.element.id, isMultiSelectMode);

                if (!isMultiSelectMode) {
                    setAnchor(item.element.id);
                }
            }
        },
        [manipulationRenderer, items, handleShiftClick, setAnchor],
    );

    const handleResizeEnd = useCallback(
        (item, elementRef, leftPercentage, width, renderOrder) => {
            if (!isResizing.current) {
                return;
            }

            isResizing.current = false;
            const { element } = item;
            snapOnDragEnd();
            onLineResizeEnd();
            onStatsResizeEnd();
            const { duration: elemDuration, startFrame } = computeResizeSnapResults(
                elements,
                getSnapPoint(),
                element.id,
                leftPercentage,
                width,
                duration,
            );

            const elementLeft = `${(startFrame / duration) * 100}%`;
            const elementWidth = `${(elemDuration / duration) * 100}%`;

            elementRef.current.style.left = elementLeft;
            elementRef.current.style.width = elementWidth;

            creativeModel.updateElement(element.id, {
                duration: elemDuration,
                startFrame,
                renderOrder,
            });
        },
        [elements, snapOnDragEnd, onLineResizeEnd, onStatsResizeEnd, getSnapPoint, creativeModel, duration],
    );

    const handleDrag = useCallback(
        (item, left, boundingBox) => {
            isDragging.current = true;
            const { element } = item;
            snapOnDrag(element.id, element.parent?.id, null, boundingBox, left);
        },
        [snapOnDrag],
    );

    const handleDrop = useCallback(
        (item, top, leftPercentage, dropParams, isBetweenTracks) => {
            // POC - move multiple selected elements
            const topLevelSelectedElements = manipulationRenderer.getTopLevelSelectedElements();

            const isSelectionMove =
                isMultipleSelectionMovePocEnabled &&
                topLevelSelectedElements.length > 1 &&
                manipulationRenderer.selectedElements.has(item.element);

            if (isSelectionMove) {
                const oldStartFrame = item.element.startFrame;
                const newStartFrame = leftToStartFrame(duration, leftPercentage);
                const delta = newStartFrame - oldStartFrame;

                creativeModel.beginAccumulation();

                for (const el of topLevelSelectedElements) {
                    creativeModel.updateElement(el.id, { startFrame: el.startFrame + delta });
                }

                creativeModel.endAccumulation();

                isDragging.current = false;
                snapOnDragEnd();

                return;
            }
            // end POC

            let elementId = item.element.id;

            if (!isDragging.current === true) {
                manipulationRenderer.selectElement(elementId);

                return;
            }

            isDragging.current = false;

            snapOnDragEnd();
            const { groupId, onElement, onGroup } = dropParams;
            const snapPoint = getSnapPoint();
            const [renderOrder, startFrame] = computeDropResults(
                elements,
                renderAbleItems,
                duration,
                elementId,
                top,
                leftPercentage,
                groupId,
                isBetweenTracks,
                snapPoint,
            );

            const command = getDropCommand(dropParams);

            switch (command) {
                case 'createGroup': {
                    const draggedParent = item.element.parent;
                    const isDesGroup = draggedParent?.onlyAllowOneSelection;
                    const activeChildBeingGrouped =
                        isDesGroup &&
                        (draggedParent.activeDropdownElementId === elementId ||
                            draggedParent.activeDropdownElementId === Number(onElement));

                    creativeModel.beginAccumulation();
                    creativeModel.updateElement(elementId, { renderOrder: renderOrder + 0.5, startFrame });
                    creativeModel.updateElement(Number(onElement), { renderOrder: renderOrder - 0.5 });
                    const newGroupEl = creativeModel.wrapElementsIntoGroup([Number(onElement), elementId]);

                    if (activeChildBeingGrouped) {
                        creativeModel.updateElement(draggedParent.id, {
                            activeDropdownElementId: newGroupEl.id,
                        });
                    }

                    creativeModel.endAccumulation();
                    manipulationRenderer.selectElement(newGroupEl.id);
                    break;
                }
                case 'addToGroup': {
                    const desUpdate = getDesUpdateForChildRemoval(item.element);

                    creativeModel.beginAccumulation();
                    creativeModel.updateElement(elementId, {
                        renderOrder,
                        startFrame,
                    });

                    if (elementId !== Number(groupId)) {
                        creativeModel.addExistingElementToGroup(elementId, Number(groupId));
                    }

                    if (desUpdate) {
                        creativeModel.updateElement(desUpdate.desGroupId, {
                            activeDropdownElementId: desUpdate.newActiveChildId,
                        });
                    }

                    creativeModel.endAccumulation();
                    manipulationRenderer.selectElement(groupId);

                    break;
                }
                case 'update':
                default: {
                    const changeParent = item.element.parent && !onGroup && item.element.parent.id !== Number(groupId);
                    const desUpdate = changeParent ? getDesUpdateForChildRemoval(item.element) : null;

                    manipulationRenderer.deselectElement(elementId);
                    creativeModel.beginAccumulation();

                    if (changeParent) {
                        elementId = creativeModel.unwrapElementFromGroup(elementId).id;
                    }

                    creativeModel.updateElement(elementId, {
                        startFrame,
                        renderOrder,
                    });

                    if (desUpdate) {
                        creativeModel.updateElement(desUpdate.desGroupId, {
                            activeDropdownElementId: desUpdate.newActiveChildId,
                        });
                    }

                    creativeModel.endAccumulation();
                    break;
                }
            }

            manipulationRenderer.selectElement(elementId, false);
        },
        [getSnapPoint, renderAbleItems, creativeModel, manipulationRenderer],
    );

    const handleCancelDragging = useCallback(() => {
        isDragging.current = false;
        snapOnDragEnd();
    }, [snapOnDragEnd]);

    const handleResize = useCallback(
        (item, fromRight, left, width, leftPercentage, widthPercentage, containerBoundingBox, boundingBox) => {
            isResizing.current = true;
            const { element } = item;
            snapOnDrag(element.id, element.parent?.id, fromRight, boundingBox, left, width);
            onLineResize(fromRight, left, width);
            onStatsResize(
                element,
                fromRight,
                left,
                width,
                leftPercentage,
                widthPercentage,
                containerBoundingBox,
                boundingBox,
            );
        },
        [snapOnDrag, onLineResize, onStatsResize],
    );

    return (
        <TimelineTracksContainer ref={timelineTracksContainerRef}>
            <TimelineTimestampsContainer>
                <Timestamps
                    duration={duration}
                    elementContainerRef={elementContainerRef}
                    timestampsContainerRef={timestampsContainerRef}
                />
            </TimelineTimestampsContainer>
            <TimelineTracksWrapper>
                <TimelineTracksList ref={tracksRef}>
                    {tracks.map((track, idx) => (
                        <Track
                            key={track.id}
                            track={track}
                            hasSeparationLine={audioTracks.length > 0 && idx === tracks.length - 1}
                        />
                    ))}
                    {audioTracks.map((track) => (
                        <Track key={track.id} track={track} />
                    ))}
                </TimelineTracksList>
                <TimelineElementsContainer ref={elementWrapperRef}>
                    <TimelineElements ref={elementContainerRef}>
                        {renderAbleItems
                            .filter((item) => item.background)
                            .map((item) => (
                                <TimelineGroupBackground
                                    key={item.element.id}
                                    style={{
                                        ...item.background,
                                        // 3px - space of each REAL element from the top
                                        top: `calc(${item.background.top} + 3px)`,
                                    }}
                                    data-group-id={item.element.id}
                                />
                            ))}
                        <DraggingProvider reactToPeers={isMultipleSelectionMovePocEnabled}>
                            {renderAbleItems.map((item) => (
                                <Element
                                    key={item.element.id}
                                    item={item}
                                    containerRef={elementContainerRef}
                                    tracksRef={tracksRef}
                                    onClick={handleSelection}
                                    onExpandClick={handleGroupExpand}
                                    onAnimationExpand={handleAnimationExpand}
                                    onResize={handleResize}
                                    onResizeEnd={handleResizeEnd}
                                    onDrag={handleDrag}
                                    onDrop={handleDrop}
                                    onCancelDragging={handleCancelDragging}
                                    trackDuration={duration} // we pass actual duration here in case of overlapping of content to prevent issues with animations timeline
                                    scrollContainerRef={timelineTracksContainerRef}
                                />
                            ))}
                        </DraggingProvider>
                        <TimelineResizingLine ref={resizeLineRef} />
                        <TimelineSnapLine ref={snapLineRef} />
                        <ResizeStats resizeStatsRef={resizeStatsRef} />
                        <TimelineOverflowOverlay />
                        <Playhead
                            containerRef={elementWrapperRef}
                            elementContainerRef={elementContainerRef}
                            duration={duration}
                            timelineDuration={timelineDuration}
                            scrollContainerRef={timelineTracksContainerRef}
                        />
                    </TimelineElements>
                </TimelineElementsContainer>
            </TimelineTracksWrapper>
        </TimelineTracksContainer>
    );
};

export default Tracks;
